// ─────────────────────────────────────────────
//  ConditionPicker — post / update / clear a room condition (Phase 5.2).
//  Modal-based (shared Modal = focus trap + Escape + ARIA), five single-select
//  kind chips + an optional operational note (200-char cap, live counter,
//  always-visible PHI nudge per C-008/D5). All actions are full-size Buttons —
//  the compact board ConditionChip is never a tap target.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { CONDITION_KINDS, NOTE_MAX, getConditionMeta } from "../domain/conditions";
import Modal from "./Modal";
import Button from "./Button";
import tokens from "../theme/tokens";

const { color, font, radius, space } = tokens;

export default function ConditionPicker({
  isOpen,
  onClose,
  roomName = "",
  condition = null, // current room.condition (or null)
  busy = false,
  onPost,  // async ({ kind, note })
  onClear, // async ()
}) {
  const [kind, setKind] = useState(condition?.kind || "on_time");
  const [note, setNote] = useState(condition?.note || "");

  // Re-sync the form whenever the picker opens on a (possibly new) condition.
  useEffect(() => {
    if (isOpen) {
      setKind(condition?.kind || "on_time");
      setNote(condition?.note || "");
    }
  }, [isOpen, condition]);

  const hasCondition = !!condition?.kind;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Room Condition"
      footer={
        <>
          {hasCondition && (
            <Button variant="secondary" onClick={onClear} disabled={busy}>
              Clear Condition
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onPost({ kind, note })} disabled={busy}>
            {busy ? "Saving…" : hasCondition ? "Update Condition" : "Post Condition"}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: font.size.body, color: color.text.muted, marginBottom: space.md }}>
        {roomName ? `${roomName} — ` : ""}Let your supervising doctor know what's happening in this room.
      </div>

      <div role="radiogroup" aria-label="Condition" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: space.md }}>
        {CONDITION_KINDS.map((k) => {
          const meta = getConditionMeta(k);
          const selected = k === kind;
          return (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setKind(k)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                minHeight: 44,
                padding: "8px 14px",
                borderRadius: radius.sm,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: font.size.body,
                fontWeight: font.weight.bold,
                background: selected ? meta.bg : color.surface.card,
                color: selected ? meta.text : color.text.secondary,
                border: selected ? `2px solid ${meta.border}` : `1px solid ${color.border.default}`,
              }}
            >
              <span aria-hidden="true">{meta.icon}</span> {meta.label}
            </button>
          );
        })}
      </div>

      <label
        htmlFor="condition-note"
        style={{ display: "block", fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, marginBottom: 6 }}
      >
        Note (optional)
      </label>
      <textarea
        id="condition-note"
        value={note}
        maxLength={NOTE_MAX}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: color.surface.raised,
          border: `1px solid ${color.border.default}`,
          color: color.text.primary,
          borderRadius: radius.sm,
          padding: "9px 12px",
          fontSize: font.size.body,
          fontFamily: "inherit",
          resize: "vertical",
        }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4 }}>
        <span style={{ fontSize: font.size.label, color: color.text.muted }}>
          Operational notes only — no patient names or identifiers. {NOTE_MAX} characters max.
        </span>
        <span style={{ fontSize: font.size.label, color: color.text.muted, whiteSpace: "nowrap" }}>
          {note.length}/{NOTE_MAX} characters
        </span>
      </div>
    </Modal>
  );
}
